import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveTemplates, FormTemplate } from '../../services/templateService';
import { getFlocs } from '../../services/flocService';
import { getAssetsByFloc } from '../../services/assetService';
import { createInspection } from '../../services/inspectionService';
import { FunctionalLocation, Asset } from '@easydone/shared';

export function NewInspectionPage() {
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if organization is in FLOC-only mode
  const inspectionMode = currentOrg?.settings?.inspection?.mode || 'asset-primary';
  const isFlocOnlyMode = inspectionMode === 'floc-only';

  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [flocs, setFlocs] = useState<FunctionalLocation[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<Asset[]>([]);

  const [targetType, setTargetType] = useState<'floc' | 'asset'>(isFlocOnlyMode ? 'floc' : 'asset');
  const [activityClass, setActivityClass] = useState<'inspection' | 'maintenance'>('inspection');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedFloc, setSelectedFloc] = useState('');
  const [selectedAsset, setSelectedAsset] = useState('');
  const [scheduledDate, setScheduledDate] = useState(() => {
    // Default to today's date in YYYY-MM-DD format
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [startImmediately, setStartImmediately] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [createPlaceholderAsset, setCreatePlaceholderAsset] = useState(false);

  // FLOC filtering state
  const [flocSearch, setFlocSearch] = useState('');
  const [flocTypeFilter, setFlocTypeFilter] = useState('');

  // Geolocation state
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationRadius, setLocationRadius] = useState(25); // Default 25m
  const [locationError, setLocationError] = useState('');

  const [loading, setLoading] = useState(true);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [currentOrg]);

  // Load assets when FLOC is selected
  useEffect(() => {
    if (selectedFloc && currentOrg) {
      loadFlocAssets(selectedFloc);
    } else {
      setAssets([]);
      setFilteredAssets([]);
    }
  }, [selectedFloc, currentOrg]);

  // Filter assets by search term
  useEffect(() => {
    if (!assets.length) {
      setFilteredAssets([]);
      return;
    }

    let filtered = [...assets];

    if (assetSearch.trim()) {
      const searchLower = assetSearch.toLowerCase();
      filtered = filtered.filter(asset =>
        asset.name.toLowerCase().includes(searchLower) ||
        asset.assetId.toLowerCase().includes(searchLower) ||
        asset.serialNumber.toLowerCase().includes(searchLower) ||
        asset.type?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredAssets(filtered);
  }, [assetSearch, assets]);

  // Pre-fill FLOC from URL as soon as flocs load
  useEffect(() => {
    if (!flocs.length) return;

    const flocId = searchParams.get('flocId');
    console.log('=== FLOC Pre-fill useEffect ===');
    console.log('flocId from URL:', flocId);
    console.log('flocs.length:', flocs.length);

    if (flocId) {
      const flocExists = flocs.some(f => f.id === flocId);
      console.log('Does flocId exist in flocs array?', flocExists);

      if (flocExists) {
        const floc = flocs.find(f => f.id === flocId);
        console.log('Found FLOC:', floc?.name, floc?.code);
        console.log('Setting selectedFloc to:', flocId);
        setSelectedFloc(flocId);
      } else {
        console.log('FLOC NOT FOUND in flocs array');
        console.log('Available FLOC IDs:', flocs.slice(0, 5).map(f => ({ id: f.id, name: f.name })));
      }
    }
  }, [flocs, searchParams]);

  // Pre-fill template and other params when templates load
  useEffect(() => {
    if (!templates.length) return;

    const templateId = searchParams.get('templateId');
    const inspectNow = searchParams.get('inspectNow');

    console.log('=== Template Pre-fill useEffect ===');
    console.log('templateId from URL:', templateId);
    console.log('templates.length:', templates.length);

    if (templateId && templates.some(t => t.id === templateId)) {
      setSelectedTemplate(templateId);
      console.log('Set selectedTemplate to:', templateId);
    }

    if (inspectNow === 'true') {
      setStartImmediately(true);
    }
  }, [templates, searchParams]);

  // Pre-fill asset when assets load
  useEffect(() => {
    const assetId = searchParams.get('assetId');
    if (!assetId || !assets.length) return;

    console.log('=== Asset Pre-fill useEffect ===');
    console.log('assetId from URL:', assetId);

    if (assets.some(a => a.id === assetId)) {
      setSelectedAsset(assetId);
      console.log('Set selectedAsset to:', assetId);
    }
  }, [assets, searchParams]);

  const loadData = async () => {
    if (!currentOrg) return;

    setLoading(true);
    try {
      const [templatesData, flocsData] = await Promise.all([
        getActiveTemplates(currentOrg.id),
        getFlocs(currentOrg.id),
      ]);
      setTemplates(templatesData);
      setFlocs(flocsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadFlocAssets = async (flocId: string) => {
    if (!currentOrg) return;

    setLoadingAssets(true);
    setAssetSearch('');
    try {
      // Get assets in this FLOC plus the installed asset if any
      const flocAssetsData = await getAssetsByFloc(currentOrg.id, flocId);
      setAssets(flocAssetsData);
      setFilteredAssets(flocAssetsData);
    } catch (error) {
      console.error('Failed to load assets:', error);
      setError('Failed to load assets for this FLOC');
      setAssets([]);
      setFilteredAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleCreate = async () => {
    console.log('=== Create button clicked ===');

    if (!currentOrg || !currentUser) {
      console.error('Missing org or user:', { currentOrg, currentUser });
      setError('User or organization not found. Please refresh the page.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validation
    if (!selectedTemplate) {
      setError('Please select an inspection template');
      console.log('Validation failed: No template selected');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (targetType === 'floc' && !selectedFloc) {
      setError('Please select a functional location');
      console.log('Validation failed: No FLOC selected');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (targetType === 'asset' && !isFlocOnlyMode && !selectedAsset && !createPlaceholderAsset) {
      setError('Please select an asset or check "Create placeholder asset"');
      console.log('Validation failed: No asset selected');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    console.log('Validation passed, creating inspection...');
    setCreating(true);
    setError('');

    try {
      let assetIdToUse = selectedAsset;

      // Create placeholder asset if requested
      if (targetType === 'asset' && createPlaceholderAsset && !selectedAsset) {
        console.log('Creating placeholder asset for FLOC...');
        const { createPlaceholderAsset: createAssetFn } = await import('../../services/assetService');
        const { commissionAsset } = await import('../../services/assetInstallationService');

        const selectedFlocObj = flocs.find(f => f.id === selectedFloc);
        if (!selectedFlocObj) {
          throw new Error('Selected FLOC not found');
        }

        // Create the placeholder asset
        const placeholderAsset = await createAssetFn(
          currentOrg.id,
          selectedFlocObj.name,
          selectedFlocObj.code,
          currentUser.uid
        );

        // Commission it to the FLOC
        await commissionAsset(
          currentOrg.id,
          {
            assetId: placeholderAsset.id,
            flocId: selectedFloc,
          },
          currentUser.uid,
          currentUser.displayName || currentUser.email || 'Unknown'
        );

        assetIdToUse = placeholderAsset.id;
        console.log('Placeholder asset created and commissioned:', placeholderAsset.id);
      }

      console.log('Creating inspection with:', {
        orgId: currentOrg.id,
        targetType,
        targetId: targetType === 'floc' ? selectedFloc : assetIdToUse,
        flocId: selectedFloc,
        assetId: targetType === 'asset' ? assetIdToUse : undefined,
        activityClass,
        templateId: selectedTemplate,
        startImmediately,
      });

      const inspection = await createInspection(
        currentOrg.id,
        {
          templateId: selectedTemplate,
          targetType: targetType,
          targetId: targetType === 'floc' ? selectedFloc : assetIdToUse!,
          flocId: selectedFloc,
          assetId: targetType === 'asset' ? assetIdToUse : undefined,
          activityClass: activityClass,
          scheduledDate: scheduledDate ? new Date(scheduledDate) : undefined,
        },
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown'
      );

      console.log('Inspection created:', inspection);

      // If startImmediately is true, start the inspection right away
      if (startImmediately) {
        console.log('Starting inspection immediately...');
        const { startInspection } = await import('../../services/inspectionService');
        await startInspection(currentOrg.id, inspection.id);
        console.log('Inspection started');
      }

      navigate(`/inspections/${inspection.id}`);
    } catch (err: any) {
      console.error('Failed to create inspection:', err);
      const errorMessage = err?.message || err?.toString() || 'Failed to create inspection';
      setError(`Error: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  // Calculate distance between two GPS coordinates using Haversine formula
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Request geolocation permission and get current location
  const enableLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationEnabled(true);
        setLocationError('');
      },
      (error) => {
        setLocationEnabled(false);
        setCurrentLocation(null);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied. Please enable location access in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out.');
            break;
          default:
            setLocationError('An unknown error occurred while getting location.');
        }
      }
    );
  };

  // Disable location filtering
  const disableLocation = () => {
    setLocationEnabled(false);
    setCurrentLocation(null);
    setLocationError('');
  };

  // Filter FLOCs - exclude hidden ones first, but always include the pre-selected FLOC
  const flocIdFromUrl = searchParams.get('flocId');
  let visibleFlocs = flocs.filter(floc => !floc.hidden || floc.id === flocIdFromUrl || floc.id === selectedFloc);

  console.log('=== Render phase ===');
  console.log('selectedFloc state:', selectedFloc);
  console.log('flocIdFromUrl:', flocIdFromUrl);
  console.log('total flocs:', flocs.length);
  console.log('visibleFlocs:', visibleFlocs.length);
  console.log('locationEnabled:', locationEnabled);

  // Apply text search filter
  if (flocSearch.trim()) {
    const searchLower = flocSearch.toLowerCase();
    visibleFlocs = visibleFlocs.filter(floc =>
      floc.name.toLowerCase().includes(searchLower) ||
      floc.code?.toLowerCase().includes(searchLower) ||
      floc.description?.toLowerCase().includes(searchLower)
    );
  }

  // Apply type filter
  if (flocTypeFilter) {
    visibleFlocs = visibleFlocs.filter(floc => floc.type === flocTypeFilter);
  }

  // Apply location filtering if enabled, but always include the pre-selected FLOC
  let filteredFlocs = locationEnabled && currentLocation
    ? visibleFlocs.filter((floc) => {
        if (!floc.location) return false;
        const distance = calculateDistance(
          currentLocation.latitude,
          currentLocation.longitude,
          floc.location.latitude,
          floc.location.longitude
        );
        return distance <= locationRadius;
      }).sort((a, b) => {
        if (!a.location || !b.location) return 0;
        const distA = calculateDistance(
          currentLocation.latitude,
          currentLocation.longitude,
          a.location.latitude,
          a.location.longitude
        );
        const distB = calculateDistance(
          currentLocation.latitude,
          currentLocation.longitude,
          b.location.latitude,
          b.location.longitude
        );
        return distA - distB;
      })
    : visibleFlocs;

  // Ensure the pre-selected FLOC is always in the list, even if it's outside the radius
  // or doesn't have location data
  if (selectedFloc && !filteredFlocs.some(f => f.id === selectedFloc)) {
    console.log('Pre-selected FLOC not in filteredFlocs, adding it...');
    const preSelectedFloc = flocs.find(f => f.id === selectedFloc);
    if (preSelectedFloc) {
      console.log('Adding pre-selected FLOC:', preSelectedFloc.name);
      filteredFlocs = [preSelectedFloc, ...filteredFlocs];
    } else {
      console.log('ERROR: Could not find pre-selected FLOC in flocs array!');
    }
  }

  console.log('filteredFlocs final count:', filteredFlocs.length);
  console.log('Is selectedFloc in filteredFlocs?', filteredFlocs.some(f => f.id === selectedFloc));
  if (selectedFloc) {
    const selectedInList = filteredFlocs.find(f => f.id === selectedFloc);
    console.log('Selected FLOC in dropdown list:', selectedInList ? selectedInList.name : 'NOT FOUND');
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const selectedFlocObj = flocs.find(f => f.id === selectedFloc);
  const selectedAssetObj = assets.find(a => a.id === selectedAsset);

  // Get unique FLOC types for filter dropdown
  const flocTypes = [...new Set(flocs.map(f => f.type).filter(Boolean))].sort();

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">New Inspection</h1>
        <p className="text-gray-600 mt-1">
          Create a new inspection or maintenance activity
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Warning if no templates */}
      {!loading && templates.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900">No Inspection Templates Available</h3>
              <p className="text-sm text-amber-700 mt-1">
                You need to create at least one inspection template before you can create inspections.
              </p>
              <button
                onClick={() => navigate('/templates')}
                className="mt-3 text-sm font-medium text-amber-900 hover:text-amber-700 underline"
              >
                Go to Inspection Templates →
              </button>
            </div>
          </div>
        </div>
      )}

      {isFlocOnlyMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-900">
            <strong>FLOC-Only Mode:</strong> Inspections are tied directly to functional locations.
            Asset tracking is disabled for this organization.
          </p>
        </div>
      )}

      <div className="card space-y-6">
        {/* Activity Class */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Activity Type *
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="inspection"
                checked={activityClass === 'inspection'}
                onChange={(e) => setActivityClass(e.target.value as 'inspection')}
                className="mr-2"
              />
              <span className="text-sm">Inspection</span>
              <span className="text-xs text-gray-500 ml-2">(Assess condition, no work)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="maintenance"
                checked={activityClass === 'maintenance'}
                onChange={(e) => setActivityClass(e.target.value as 'maintenance')}
                className="mr-2"
              />
              <span className="text-sm">Maintenance</span>
              <span className="text-xs text-gray-500 ml-2">(Perform work, improve condition)</span>
            </label>
          </div>
        </div>

        {/* Target Type - Only show in asset-primary mode */}
        {!isFlocOnlyMode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Inspection Target *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="asset"
                  checked={targetType === 'asset'}
                  onChange={(e) => setTargetType(e.target.value as 'asset')}
                  className="mr-2"
                />
                <span className="text-sm">Asset</span>
                <span className="text-xs text-gray-500 ml-2">(Specific equipment)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="floc"
                  checked={targetType === 'floc'}
                  onChange={(e) => setTargetType(e.target.value as 'floc')}
                  className="mr-2"
                />
                <span className="text-sm">FLOC</span>
                <span className="text-xs text-gray-500 ml-2">(Location/position)</span>
              </label>
            </div>
          </div>
        )}

        {/* Template Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Inspection Template *
          </label>
          {templates.length === 0 ? (
            <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
              No inspection templates available. Please create an inspection template first.
            </div>
          ) : (
            <select
              value={selectedTemplate}
              onChange={e => setSelectedTemplate(e.target.value)}
              className="input w-full"
            >
              <option value="">Select an inspection template...</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} - {template.category}
                </option>
              ))}
            </select>
          )}
          {selectedTemplate && (
            <div className="mt-2 text-sm text-gray-600">
              {templates.find(t => t.id === selectedTemplate)?.description}
            </div>
          )}
        </div>

        {/* FLOC Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Functional Location *
          </label>

          {/* Advanced Filtering Controls */}
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">Filters</span>
              {(flocSearch || flocTypeFilter) && (
                <button
                  type="button"
                  onClick={() => {
                    setFlocSearch('');
                    setFlocTypeFilter('');
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Clear All
                </button>
              )}
            </div>

            {/* Search and Type Filter Row */}
            <div className="grid grid-cols-2 gap-2">
              {/* Search */}
              <div>
                <label className="block text-xs text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={flocSearch}
                  onChange={(e) => setFlocSearch(e.target.value)}
                  placeholder="Name or code..."
                  className="input input-sm w-full text-sm"
                />
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-xs text-gray-700 mb-1">Type</label>
                <select
                  value={flocTypeFilter}
                  onChange={(e) => setFlocTypeFilter(e.target.value)}
                  className="input input-sm w-full text-sm"
                >
                  <option value="">All Types</option>
                  {flocTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results count */}
            <div className="text-xs text-gray-600 pt-1">
              Showing {filteredFlocs.length} of {flocs.length} FLOCs
            </div>
          </div>

          {/* Location Filtering Controls */}
          <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Filter by Location</span>
              {!locationEnabled ? (
                <button
                  type="button"
                  onClick={enableLocation}
                  className="text-xs btn btn-secondary py-1 px-3"
                >
                  Enable Location
                </button>
              ) : (
                <button
                  type="button"
                  onClick={disableLocation}
                  className="text-xs btn btn-secondary py-1 px-3"
                >
                  Disable
                </button>
              )}
            </div>

            {locationError && (
              <div className="text-xs text-red-600 mb-2">{locationError}</div>
            )}

            {locationEnabled && currentLocation && (
              <div className="mt-2">
                <label className="block text-xs text-gray-600 mb-1">
                  Radius: {locationRadius}m ({filteredFlocs.length} FLOCs nearby)
                </label>
                <select
                  value={locationRadius}
                  onChange={(e) => setLocationRadius(Number(e.target.value))}
                  className="input input-sm w-full text-xs"
                >
                  <option value={10}>10 meters</option>
                  <option value={25}>25 meters</option>
                  <option value={50}>50 meters</option>
                  <option value={100}>100 meters</option>
                  <option value={250}>250 meters</option>
                  <option value={1000}>1000 meters (1km)</option>
                </select>
              </div>
            )}

            {locationEnabled && filteredFlocs.length === 0 && (
              <div className="text-xs text-yellow-600 mt-2">
                No FLOCs found within {locationRadius}m. Try increasing the radius or disable location filtering.
              </div>
            )}
          </div>

          {flocs.length === 0 ? (
            <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
              No functional locations available. Please create a FLOC first.
            </div>
          ) : (
            <select
              value={selectedFloc}
              onChange={e => setSelectedFloc(e.target.value)}
              className="input w-full"
            >
              <option value="">Select a functional location...</option>
              {filteredFlocs.map((floc) => {
                let distance = '';
                if (locationEnabled && currentLocation && floc.location) {
                  const distanceMeters = calculateDistance(
                    currentLocation.latitude,
                    currentLocation.longitude,
                    floc.location.latitude,
                    floc.location.longitude
                  );
                  distance = ` - ${distanceMeters.toFixed(0)}m away`;
                }
                return (
                  <option key={floc.id} value={floc.id}>
                    {floc.name} ({floc.code}) - {floc.type}{distance}
                  </option>
                );
              })}
            </select>
          )}
          {selectedFlocObj && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm">
                <span className="font-medium">Criticality:</span>{' '}
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  selectedFlocObj.criticality === 'very_high' ? 'bg-red-100 text-red-800' :
                  selectedFlocObj.criticality === 'high' ? 'bg-orange-100 text-orange-800' :
                  selectedFlocObj.criticality === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  selectedFlocObj.criticality === 'low' ? 'bg-blue-100 text-blue-800' :
                  selectedFlocObj.criticality === 'very_low' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {selectedFlocObj.criticality.toUpperCase()}
                </span>
              </div>
              {selectedFlocObj.installedAssetId && (
                <div className="text-sm mt-1 text-blue-700">
                  ✓ Asset installed at this location
                </div>
              )}
            </div>
          )}
        </div>

        {/* Asset Selection (only if target is asset AND not in FLOC-only mode) */}
        {targetType === 'asset' && !isFlocOnlyMode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asset *
            </label>
            {!selectedFloc ? (
              <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
                Please select a functional location first
              </div>
            ) : loadingAssets ? (
              <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
                Loading assets...
              </div>
            ) : assets.length === 0 ? (
              <div>
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 p-4 rounded-lg mb-3">
                  <p className="font-medium mb-2">No assets at this location</p>
                  <p className="text-xs text-gray-500">
                    You can commission an existing asset to this FLOC, or create a placeholder asset below.
                  </p>
                </div>

                {/* Checkbox to create placeholder asset */}
                {(currentOrg?.settings?.inspection?.autoCreateAssets || true) && (
                  <label className="flex items-start p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={createPlaceholderAsset}
                      onChange={(e) => setCreatePlaceholderAsset(e.target.checked)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">Create placeholder asset for this FLOC</div>
                      <p className="text-xs text-gray-600 mt-1">
                        A temporary asset will be created with the FLOC's name. You can update it later with equipment details.
                      </p>
                    </div>
                  </label>
                )}
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={assetSearch}
                  onChange={e => setAssetSearch(e.target.value)}
                  placeholder="Search by name, serial, or type..."
                  className="input w-full mb-2"
                />
                <select
                  value={selectedAsset}
                  onChange={e => setSelectedAsset(e.target.value)}
                  className="input w-full"
                >
                  <option value="">Select an asset...</option>
                  {filteredAssets.map(asset => (
                    <option key={asset.id} value={asset.id}>
                      {asset.name} (SN: {asset.serialNumber}) - Condition: {asset.conditionScore} ({asset.conditionGrade})
                    </option>
                  ))}
                </select>
                {assetSearch && (
                  <p className="text-xs text-gray-600 mt-1">
                    Showing {filteredAssets.length} of {assets.length} assets
                  </p>
                )}
              </>
            )}
            {selectedAssetObj && (
              <div className="mt-2 p-3 bg-green-50 rounded-lg">
                <div className="text-sm">
                  <span className="font-medium">Current Condition:</span>{' '}
                  <span className={`text-lg font-bold ${
                    selectedAssetObj.conditionScore >= 80 ? 'text-green-600' :
                    selectedAssetObj.conditionScore >= 60 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {selectedAssetObj.conditionScore}
                  </span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded bg-gray-200">
                    Grade {selectedAssetObj.conditionGrade}
                  </span>
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Type: {selectedAssetObj.type}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Scheduled Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Scheduled Date
          </label>
          <input
            type="date"
            value={scheduledDate}
            onChange={e => setScheduledDate(e.target.value)}
            className="input w-full"
          />
          <p className="text-xs text-gray-500 mt-1">
            Defaults to today's date
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <button
            onClick={() => navigate('/inspections')}
            className="btn btn-secondary"
            disabled={creating}
          >
            Cancel
          </button>
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={handleCreate}
              className="btn btn-primary"
              disabled={
                creating ||
                !selectedTemplate ||
                !selectedFloc ||
                (targetType === 'asset' && !isFlocOnlyMode && !selectedAsset && !createPlaceholderAsset)
              }
              title={
                !selectedTemplate ? 'Please select an inspection template' :
                !selectedFloc ? 'Please select a functional location' :
                (targetType === 'asset' && !isFlocOnlyMode && !selectedAsset && !createPlaceholderAsset) ? 'Please select an asset' :
                ''
              }
            >
              {creating ? 'Creating...' : startImmediately ? 'Start Now' : 'Create'}
            </button>
            {(creating || !selectedTemplate || !selectedFloc || (targetType === 'asset' && !isFlocOnlyMode && !selectedAsset && !createPlaceholderAsset)) && !creating && (
              <p className="text-xs text-gray-500">
                {!selectedTemplate ? 'Inspection template required' :
                 !selectedFloc ? 'FLOC required' :
                 (targetType === 'asset' && !isFlocOnlyMode && !selectedAsset && !createPlaceholderAsset) ? 'Asset required' :
                 ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
